import * as pdfjsLib from 'pdfjs-dist';
import { PDFDocument, degrees, StandardFonts } from 'pdf-lib';
import { PageEdits, emptyEdits, drawEditsOnCanvas, drawEditsOnPdfPage } from './pdfEdit';

// Reuse the same locally-served worker the rest of the app uses (see aiService.ts).
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker/pdf.worker.min.js';

// Target width (in px) for the rasterized page thumbnails shown in the organizer grid.
const THUMBNAIL_WIDTH = 220;

interface RenderedPage {
  canvas: HTMLCanvasElement;
  context: CanvasRenderingContext2D;
  pageWidthPt: number; // displayed page width in PDF points
  pageHeightPt: number; // displayed page height in PDF points
}

// Render a single page (1-based) to an off-screen canvas at the given target
// width, honoring the page's intrinsic rotation plus any extra user rotation.
const renderPage = async (
  pdf: any,
  pageNumber: number,
  extraRotation: number,
  targetWidth: number
): Promise<RenderedPage> => {
  const page = await pdf.getPage(pageNumber);
  const rotation = (page.rotate + extraRotation) % 360;
  const base = page.getViewport({ scale: 1, rotation }); // size in points at scale 1
  const scale = targetWidth / base.width;
  const viewport = page.getViewport({ scale, rotation });

  const canvas = document.createElement('canvas');
  canvas.width = Math.ceil(viewport.width);
  canvas.height = Math.ceil(viewport.height);
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Could not get canvas context');

  await page.render({ canvasContext: context, viewport }).promise;
  return { canvas, context, pageWidthPt: base.width, pageHeightPt: base.height };
};

/**
 * Render every page of a PDF (given its raw bytes) to a PNG data-URL thumbnail.
 * Used when a new file is added so each page gets a preview in the grid.
 */
export const renderPdfThumbnails = async (bytes: Uint8Array): Promise<string[]> => {
  // pdf.js may transfer/detach the underlying buffer, so always hand it a copy
  // and keep the caller's original bytes intact for later pdf-lib export.
  const pdf = await pdfjsLib.getDocument({ data: new Uint8Array(bytes) }).promise;
  const thumbnails: string[] = [];
  try {
    for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
      const { canvas } = await renderPage(pdf, pageNumber, 0, THUMBNAIL_WIDTH);
      thumbnails.push(canvas.toDataURL('image/png'));
    }
  } finally {
    pdf.destroy();
  }
  return thumbnails;
};

/**
 * Render a single page thumbnail with any edits (text + boxes) drawn on top. Used
 * to refresh a page's preview after it is rotated or edited.
 */
export const renderPdfPageThumbnail = async (
  bytes: Uint8Array,
  pageNumber: number,
  extraRotation: number,
  edits: PageEdits = emptyEdits()
): Promise<string> => {
  const pdf = await pdfjsLib.getDocument({ data: new Uint8Array(bytes) }).promise;
  try {
    const { canvas, context, pageWidthPt } = await renderPage(
      pdf,
      pageNumber,
      extraRotation,
      THUMBNAIL_WIDTH
    );
    drawEditsOnCanvas(context, edits, canvas.width, canvas.height, pageWidthPt);
    return canvas.toDataURL('image/png');
  } finally {
    pdf.destroy();
  }
};

export interface EditorPageRender {
  dataUrl: string; // background image of the page (no annotations baked in)
  cssWidth: number; // rendered size in CSS px
  cssHeight: number;
  pageWidthPt: number; // displayed page size in points (for px<->point scaling)
  pageHeightPt: number;
}

/**
 * Render a page at a larger size for the editor. Annotations are NOT baked in —
 * the editor overlays them as draggable HTML elements on top of this image.
 */
export const renderPdfPageForEditor = async (
  bytes: Uint8Array,
  pageNumber: number,
  extraRotation: number,
  targetWidth: number
): Promise<EditorPageRender> => {
  const pdf = await pdfjsLib.getDocument({ data: new Uint8Array(bytes) }).promise;
  try {
    const { canvas, pageWidthPt, pageHeightPt } = await renderPage(
      pdf,
      pageNumber,
      extraRotation,
      targetWidth
    );
    return {
      dataUrl: canvas.toDataURL('image/png'),
      cssWidth: canvas.width,
      cssHeight: canvas.height,
      pageWidthPt,
      pageHeightPt,
    };
  } finally {
    pdf.destroy();
  }
};

// A single page in the final document: which source file, which page in it, how
// much extra rotation the user applied, and any edits (text + boxes) to bake in.
export interface MergePageRef {
  fileId: string;
  pageIndex: number; // 0-based index within the source PDF
  rotation: number; // extra degrees (0/90/180/270) to apply on export
  edits: PageEdits;
}

/**
 * Build a single PDF from the given ordered page references, pulling each page
 * from its source PDF (looked up by fileId), applying any extra rotation, and
 * drawing on any edits. Original vector content is preserved.
 */
export const mergePdfPages = async (
  pages: MergePageRef[],
  sources: Record<string, Uint8Array>
): Promise<Blob> => {
  const output = await PDFDocument.create();
  const font = await output.embedFont(StandardFonts.Helvetica);
  // Load each source document at most once.
  const loaded = new Map<string, PDFDocument>();

  for (const ref of pages) {
    let source = loaded.get(ref.fileId);
    if (!source) {
      source = await PDFDocument.load(sources[ref.fileId]);
      loaded.set(ref.fileId, source);
    }
    const [copied] = await output.copyPages(source, [ref.pageIndex]);
    if (ref.rotation % 360 !== 0) {
      const current = copied.getRotation().angle;
      copied.setRotation(degrees((current + ref.rotation) % 360));
    }
    drawEditsOnPdfPage(copied, ref.edits, font);
    output.addPage(copied);
  }

  const bytes = await output.save();
  return new Blob([bytes], { type: 'application/pdf' });
};
